import type { TypePageBuilderSkeleton } from '~/types/contentful'
import type { ITabletopOrganization } from '~/types/tabletop'
import { DASHBOARD_ENTRY_ID } from '~/utils/const'

export interface DashboardAccessConfig {
  legacyMetricsCountries: string[]
  newProfileUserIds: string[]
  newProfileGroupIds: string[]
  newProfileParentOrgIds: string[]
}

const emptyAccessConfig = (): DashboardAccessConfig => ({
  legacyMetricsCountries: [],
  newProfileUserIds: [],
  newProfileGroupIds: [],
  newProfileParentOrgIds: [],
})

// Read the metrics access config from the /dashboard page builder
export const fetchDashboardAccessConfig =
  async (): Promise<DashboardAccessConfig> => {
    const { $client } = useNuxtApp()
    try {
      const entry =
        await $client.withoutUnresolvableLinks.getEntry<TypePageBuilderSkeleton>(
          DASHBOARD_ENTRY_ID,
        )
      const config = (entry?.fields?.config ||
        {}) as Partial<DashboardAccessConfig>
      return {
        legacyMetricsCountries: config.legacyMetricsCountries || [],
        newProfileUserIds: config.newProfileUserIds || [],
        newProfileGroupIds: config.newProfileGroupIds || [],
        newProfileParentOrgIds: config.newProfileParentOrgIds || [],
      }
    } catch (error) {
      console.error('Failed to fetch dashboard access config:', error)
      return emptyAccessConfig()
    }
  }

// Resolve whether the given store/user should see the legacy metrics view (vs v2).
// Pure function – exported so store data variations can be unit tested.
export function resolveShowLegacyMetrics({
  store,
  config,
  accountId,
}: {
  store: ITabletopOrganization | undefined
  config: DashboardAccessConfig
  accountId: string | undefined
}): boolean {
  // GameStop stores display a custom view of v2 metrics
  if (checkIsGamestopStore(store)) return false

  // Show V2 if the user or the store's group is allowlisted
  const isUserAllowlistedForV2 = Boolean(
    accountId && config.newProfileUserIds.includes(accountId),
  )
  const isGroupAllowlistedForV2 =
    store?.groups?.some((group) =>
      config.newProfileGroupIds.includes(group?.id),
    ) ?? false
  const isParentOrgAllowlistedForV2 = Boolean(
    store?.parentOrganizationId &&
      config.newProfileParentOrgIds.includes(store?.parentOrganizationId),
  )
  if (
    isUserAllowlistedForV2 ||
    isGroupAllowlistedForV2 ||
    isParentOrgAllowlistedForV2
  ) {
    return false
  }

  // Legacy countries always see legacy metrics regardless of benefits
  if (checkIsLegacyMetricsCountry(store, config.legacyMetricsCountries)) {
    return true
  }

  // Non-legacy countries: use benefits flag to decide
  if (store?.benefits === true) return false

  // Default to show legacy metrics
  return true
}

// Whether the current store/user is limited to the legacy metrics view
export const useLegacyMetricsView = () => {
  const route = useRoute()
  const userStore = useUserStore()
  const { accountId, stores } = storeToRefs(userStore)

  const { data: config } = useAsyncData(
    'dashboard-access-config',
    fetchDashboardAccessConfig,
    {
      getCachedData: (key) => useCachedData(key),
      default: emptyAccessConfig,
    },
  )

  const currentStore = computed(
    () =>
      stores.value?.find(
        (store) => store?.id === arrayAsString(route.query.s),
      ) || stores.value?.[0],
  )

  const showLegacyMetrics = computed(() =>
    resolveShowLegacyMetrics({
      store: currentStore.value,
      config: config.value,
      accountId: accountId.value,
    }),
  )

  return { config, showLegacyMetrics }
}
